import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "#/lib/queryKeys";
import {
  createArticleOutline,
  deleteArticleOutline,
  regenerateArticleOutline,
  updateArticleOutline,
  approveArticleOutline,
  generateArticleOutlineList,
  CreateArticleOutlineRequest,
  UpdateArticleOutlineRequest,
} from "#/services/articleOutlines";

type UpdatePayload = {
  id: string;
  payload: UpdateArticleOutlineRequest;
};

type ApprovePayload = {
  id: string;
  generateNow?: boolean;
  additionalInstructions?: string;
};

export function useArticleOutlineMutations() {
  const queryClient = useQueryClient();

  const createOutline = useMutation({
    mutationFn: (payload: CreateArticleOutlineRequest) =>
      createArticleOutline(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.articleOutlines() });
    },
  });

  const updateOutline = useMutation({
    mutationFn: ({ id, payload }: UpdatePayload) =>
      updateArticleOutline(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: qk.articleOutlines() });
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: qk.articleOutlineDetail(data.id),
        });
      }
    },
  });

  const removeOutline = useMutation({
    mutationFn: (id: string) => deleteArticleOutline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.articleOutlines() });
    },
  });

  const approveOutline = useMutation({
    mutationFn: ({ id, generateNow, additionalInstructions }: ApprovePayload) =>
      approveArticleOutline(id, { generateNow, additionalInstructions }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: qk.articleOutlines() });
      if (data?.outline?.id) {
        queryClient.invalidateQueries({
          queryKey: qk.articleOutlineDetail(data.outline.id),
        });
      }
    },
  });

  const generateOutlines = useMutation({
    mutationFn: (term: string) => generateArticleOutlineList(term),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.articleOutlines() });
    },
  });

  const regenerateOutline = useMutation({
    mutationFn: (id: string) => regenerateArticleOutline(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: qk.articleOutlines() });
      queryClient.invalidateQueries({ queryKey: qk.articleOutlineDetail(id) });
    },
  });

  return { createOutline, updateOutline, removeOutline, regenerateOutline, approveOutline, generateOutlines };
}

export default useArticleOutlineMutations;
